import { useReducer, useContext } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { AppContext, AppContextInterface } from "../../../contexts/AppContext";

import OrganizationProgressCard from "./OrganizationProgressCard";
import ProgramProgressCard from "./ProgramProgressCard";
import CourseProgressCard from "./CourseProgressCard";
import TabComponent from "../../common/tabComponent/TabComponent";
import { WmButton, WmTabPanel } from "@watermarkinsights/ripple-react";
import "../../common/Insights.scss";
import translate from "../../../i18n/translate";
import WidgetLoader from "../../common/elements/WidgetLoader";
import { useFormatMessage } from "@/features/common/helper";

export interface MissionAndOutcomeProps {
  onComponentLoad: (type: string) => void;
}
interface LoadedTab {
  organizations: boolean;
  programs: boolean;
  courses: boolean;
}

const allTabsLoaded = (loadedTabs: LoadedTab) => {
  return loadedTabs.organizations && loadedTabs.programs && loadedTabs.courses;
};
const initialState = {
  selectedTab: "item-1",
  loadedTabs: {
    organizations: false,
    programs: false,
    courses: false
  }
};

interface Tab {
  organizations: boolean;
  programs: boolean;
  courses: boolean;
  [key: string]: boolean;
}

interface MissionAndOutcomeState {
  selectedTab: string;
  loadedTabs: Tab;
}

interface MissionAndOutcomeAction {
  type: string;
  tab: string;
  tabId: string;
}

const reducer = (
  state: MissionAndOutcomeState,
  action: MissionAndOutcomeAction
) => {
  switch (action.type) {
    case "SetSelectedTab":
      return { ...state, selectedTab: action.tabId };
    case "setLoadedTab":
      const tab = action.tab;
      let loadedTabs = state.loadedTabs;
      loadedTabs[tab] = true;
      return { ...state, loadedTabs: loadedTabs };
    default:
      return state;
  }
};

const getURL = (institutionUuid: string) => {
  const queryString = window.location.search;
  const param = new URLSearchParams(queryString);
  const locale = param.get("locale");
  const isPlanning = window.location.pathname.includes("/planning/");
  const base = isPlanning ? `/insights/planning` : `/insights`;
  let url = `${base}/institution/${institutionUuid}/institution-strategy/html`;
  if (locale !== null) {
    url = `${url}?locale=${locale}`;
  }
  return url;
};
const openMissionAndOutcomesHTMLViewLink = (institutionUuid: string) => {
  return window.open(getURL(institutionUuid), "_blank");
};

export default function MissionAndOutcomes({
  onComponentLoad
}: MissionAndOutcomeProps) {
  const { outcomesTerminology, institution } = useContext<AppContextInterface>(AppContext);
  const formatMessage = useFormatMessage();
  const tabOption = [
    {
      name: formatMessage({ id: "app.missionAndOutcomes.organizations" }),
      id: "tab1"
    },
    {
      name: formatMessage({ id: "app.missionAndOutcomes.programs" }),
      id: "tab2"
    },
    {
      name: formatMessage({ id: "app.missionAndOutcomes.courses" }),
      id: "tab3"
    }
  ];

  const defaultAction = { type: "", tabId: "", tab: "" };

  const [state, dispatch] = useReducer(reducer, initialState);

  const onWmTabSelected = (event: any) => {
    dispatch({
      ...defaultAction,
      type: "SetSelectedTab",
      tabId: event.detail.tabId
    });
  };

  const onTabLoad = (tab: string) => {
    dispatch({ ...defaultAction, type: "setLoadedTab", tab: tab });
  };

  const getOutcomeText = () => {
    if (!outcomesTerminology) {
      return (
        <span className="heading-loader-wrapper">
          <WidgetLoader
            loaderContainerClasses="loader-container"
            loaderClasses="widget-loader"
          />
        </span>
      );
    }
    return outcomesTerminology?.outcomes?.plural || "...";
  };

  return (
    <div className="paper-card -withtabs paper-card-height">
      <div className="header">
        <div className="info">
          <h3 className="title">
            <FormattedMessage
              id="app.missionAndOutcomes.title"
              values={{
                outcome_plural: getOutcomeText()
              }}
            />
          </h3>
          <p className="description">
            {translate("app.missionAndOutcomes.description")}
          </p>
        </div>
        <div>
          <WmButton
            id="export-mission-and-outcomes"
            tabIndex={0}
            onClick={() => openMissionAndOutcomesHTMLViewLink(institution?.platformUuid ?? "")}
            button-type="secondary"
          >
            {translate("app.exportHtml")}
          </WmButton>
        </div>
      </div>
      <div className="body" id="tab-component">
        {!allTabsLoaded(state.loadedTabs) && (
          <div className="loader-position">
            <WidgetLoader
              loaderContainerClasses="loader-container"
              loaderClasses="widget-loader"
            />
          </div>
        )}
        {allTabsLoaded(state.loadedTabs) && (
          <div className="tabbar">
            <TabComponent
              selectedTab={state.selectedTab}
              onWmTabSelected={onWmTabSelected}
              tabOption={tabOption}
            />
          </div>
        )}
        <WmTabPanel className="tab-panel" id="tab-one" tabId={"item-1"}>
          <OrganizationProgressCard
            onTabLoad={onTabLoad}
            onComponentLoad={onComponentLoad}
          />
        </WmTabPanel>
        <WmTabPanel className="tab-panel" id="tab-two" tabId={"item-2"}>
          <ProgramProgressCard
            onTabLoad={onTabLoad}
            onComponentLoad={onComponentLoad}
          />
        </WmTabPanel>
        <WmTabPanel className="tab-panel" id="tab-three" tabId={"item-3"}>
          <CourseProgressCard
            onTabLoad={onTabLoad}
            onComponentLoad={onComponentLoad}
          />
        </WmTabPanel>
      </div>
    </div>
  );
}
